import React, { useState, useEffect } from 'react';
import { notificationsAPI } from '../services/api';

function Banners() {
  const [showForm, setShowForm] = useState(false);
  const [editingBannerId, setEditingBannerId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    action_url: '',
    banner_type: 'promotional',
    target_audience: 'all',
    priority: 0,
    is_active: true,
    start_date: '',
    end_date: '',
  });
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  const getEmptyFormData = () => ({
    title: '',
    description: '',
    image_url: '',
    action_url: '',
    banner_type: 'promotional',
    target_audience: 'all',
    priority: 0,
    is_active: true,
    start_date: '',
    end_date: '',
  });

  const formatDateTimeLocal = (value) => {
    if (!value) return '';
    const date = new Date(value);
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await notificationsAPI.getBanners();
      setBanners(response.data.data || []);
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'priority' ? parseInt(value) : value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: 'Title is required' });
      return;
    }

    try {
      if (editingBannerId) {
        await notificationsAPI.updateBanner(editingBannerId, formData);
        setMessage({ type: 'success', text: 'Banner updated successfully!' });
      } else {
        await notificationsAPI.createBanner(formData);
        setMessage({ type: 'success', text: 'Banner created successfully!' });
      }
      
      setFormData(getEmptyFormData());
      setEditingBannerId(null);
      
      setShowForm(false);
      fetchBanners();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save banner' 
      });
    }
  };

  const handleEditBanner = (banner) => {
    setEditingBannerId(banner.id);
    setFormData({
      title: banner.title || '',
      description: banner.description || '',
      image_url: banner.image_url || '',
      action_url: banner.action_url || '',
      banner_type: banner.banner_type || 'promotional',
      target_audience: banner.target_audience || 'all',
      priority: banner.priority || 0,
      is_active: Boolean(banner.is_active),
      start_date: formatDateTimeLocal(banner.start_date),
      end_date: formatDateTimeLocal(banner.end_date),
    });
    setShowForm(true);
    setMessage({ type: '', text: '' });
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm('정말 삭제하시겠습니까? 삭제하면 복구할 수 없습니다.')) {
      return;
    }

    try {
      await notificationsAPI.deleteBanner(bannerId);
      setMessage({ type: 'success', text: 'Banner deleted successfully!' });
      fetchBanners();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to delete banner' 
      });
    }
  };

  const resetForm = () => {
    setFormData(getEmptyFormData());
    setEditingBannerId(null);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Banner Management</h1>
        <p>Create and manage promotional banners</p>
        <button 
          className="btn btn-primary" 
          onClick={() => {
            if (showForm) {
              setShowForm(false);
              resetForm();
            } else {
              resetForm();
              setShowForm(true);
            }
          }}
          style={{ marginTop: '10px' }}
        >
          {showForm ? 'Cancel' : '+ Create Banner'}
        </button>
      </div>

      {message.text && (
        <div className={message.type === 'success' ? 'success' : 'error'} style={{ marginBottom: '20px' }}>
          {message.text}
        </div>
      )}

      {showForm && (
        <div className="card">
          <div className="card-header">
            <h2>{editingBannerId ? 'Edit Banner' : 'Create New Banner'}</h2>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                className="form-control"
                placeholder="Banner title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                className="form-control"
                placeholder="Banner description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label>Image URL</label>
                <input
                  type="url"
                  name="image_url"
                  className="form-control"
                  placeholder="https://example.com/image.jpg"
                  value={formData.image_url}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Action URL</label>
                <input
                  type="url"
                  name="action_url"
                  className="form-control"
                  placeholder="https://example.com/promo"
                  value={formData.action_url}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label>Banner Type</label>
                <select
                  name="banner_type"
                  className="form-control"
                  value={formData.banner_type}
                  onChange={handleInputChange}
                >
                  <option value="promotional">Promotional</option>
                  <option value="announcement">Announcement</option>
                  <option value="warning">Warning</option>
                </select>
              </div>

              <div className="form-group">
                <label>Target Audience</label>
                <select
                  name="target_audience"
                  className="form-control"
                  value={formData.target_audience}
                  onChange={handleInputChange}
                >
                  <option value="all">All Users</option>
                  <option value="new_users">New Users</option>
                  <option value="active_users">Active Users</option>
                  <option value="specific">Specific Users</option>
                </select>
              </div>

              <div className="form-group">
                <label>Priority (0-10)</label>
                <input
                  type="number"
                  name="priority"
                  className="form-control"
                  min="0"
                  max="10"
                  value={formData.priority}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="datetime-local"
                  name="start_date"
                  className="form-control"
                  value={formData.start_date}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>End Date</label>
                <input
                  type="datetime-local"
                  name="end_date"
                  className="form-control"
                  value={formData.end_date}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                Active (show immediately)
              </label>
            </div>

            <button type="submit" className="btn btn-success">
              {editingBannerId ? 'Update Banner' : 'Create Banner'}
            </button>
          </form>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>All Banners</h2>
        </div>
        
        {loading ? (
          <div className="loading">Loading banners...</div>
        ) : banners.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
            No banners created yet
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Target</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Views</th>
                  <th>Clicks</th>
                  <th>CTR</th>
                  <th>Period</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {banners.map((banner) => (
                  <tr key={banner.id}>
                    <td>{banner.id}</td>
                    <td>{banner.title}</td>
                    <td>
                      <span className="badge badge-info">
                        {banner.banner_type}
                      </span>
                    </td>
                    <td>{banner.target_audience}</td>
                    <td>{banner.priority}</td>
                    <td>
                      <span className={`badge ${banner.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {banner.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{banner.view_count}</td>
                    <td>{banner.click_count}</td>
                    <td>{banner.ctr}%</td>
                    <td>
                      {banner.start_date ? new Date(banner.start_date).toLocaleDateString() : '-'} 
                      {' to '}
                      {banner.end_date ? new Date(banner.end_date).toLocaleDateString() : '-'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button
                          className="btn btn-primary"
                          style={{ padding: '5px 10px', fontSize: '12px' }}
                          onClick={() => handleEditBanner(banner)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{ padding: '5px 10px', fontSize: '12px' }}
                          onClick={() => handleDeleteBanner(banner.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Banners;
